import { FC, MouseEvent, useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { appointmentUnitLabels } from './AppointmentCard';
import {
  AppointmentStatus,
  AvailabilityBand,
  CalendarRow,
  SOURCE_LABELS,
  UnavailableBand,
  UnitOption,
} from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Status → palette key, per foundations/status-registry.md (Appointment row). */
export const STATUS_PALETTE: Record<
  AppointmentStatus,
  'info' | 'success' | 'warning' | 'error' | 'grey'
> = {
  scheduled: 'info',
  completed: 'success',
  rescheduled: 'warning',
  no_show: 'error',
  cancelled: 'grey',
};

const ROW_MINUTES = 15;
const ROW_PX = 14;
const HOUR_COL_PX = 56;
const DEFAULT_START_HOUR = 8;
const DEFAULT_END_HOUR = 18;

export type CalendarAppointmentAction =
  'reschedule' | 'complete' | 'no_show' | 'cancel';

export interface CalendarViewProps {
  /** Monday of the week being shown (property-local date, no time). */
  weekStart: Dayjs;
  onWeekChange: (start: Dayjs) => void;
  /** IANA zone every row is rendered in. */
  timezone: string;
  appointments: CalendarRow[];
  availability: AvailabilityBand[];
  /** Time the host cannot be booked because her own calendar is busy. Shown as
   * neutral hatched bands; empty on surfaces with no synced calendar. */
  unavailable?: UnavailableBand[];
  hosts: { id: number; name: string }[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onAppointmentClick: (row: CalendarRow) => void;
  /** Click inside an availability band on empty space. */
  onSlotClick?: (hostId: number, startAt: Dayjs) => void;
  /** Compact action menu on each block. */
  onAppointmentAction?: (
    row: CalendarRow,
    action: CalendarAppointmentAction,
  ) => void;
  /** Hide the host filter (My Calendar passes a single person). */
  hideHostFilter?: boolean;
  /** Show the property name on each block (My Calendar, multi-property). */
  showProperty?: boolean;
}

const minutesSinceMidnight = (value: Dayjs): number =>
  value.hour() * 60 + value.minute();

/** Short zone label ("EDT"); falls back to the IANA name when the runtime has
 * no abbreviation for it (older ICU / jsdom). */
export const zoneAbbreviation = (tz: string, at: Date = new Date()): string => {
  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName');
    if (part?.value && !/^GMT|^UTC/.test(part.value)) {
      return part.value;
    }
  } catch {
    // fall through to the IANA name
  }
  return tz;
};

/** Week grid: 7 property-local day columns × 15-minute rows. Availability
 * bands are shaded under appointment blocks; blocks are coloured by status.
 * Hand-rolled on purpose — Loki has no calendar dependency (plan §3.10). */
/**
 * Merge one host's bands into non-overlapping windows per day.
 *
 * Every host contributes a band, so with twenty hosts twenty translucent
 * rectangles stack on top of each other and the "available" tint turns into a
 * solid block that hides the appointments. Union the windows first; each
 * merged window keeps how many hosts it covers for the tooltip.
 */
function mergeAvailabilityBands(
  bands: AvailabilityBand[],
  tz: string
): Array<{ date: string; start: Dayjs; end: Dayjs; hostCount: number }> {
  const byDay = new Map<string, Array<{ start: Dayjs; end: Dayjs }>>();
  bands.forEach((band) => {
    const start = dayjs(band.start_at).tz(tz);
    const end = dayjs(band.end_at).tz(tz);
    const key = start.format('YYYY-MM-DD');
    const rows = byDay.get(key) ?? [];
    rows.push({ start, end });
    byDay.set(key, rows);
  });

  const merged: Array<{ date: string; start: Dayjs; end: Dayjs; hostCount: number }> = [];
  byDay.forEach((rows, date) => {
    const sorted = rows
      .slice()
      .sort((a, b) => a.start.valueOf() - b.start.valueOf());
    let current = { start: sorted[0].start, end: sorted[0].end, hostCount: 1 };
    for (let i = 1; i < sorted.length; i += 1) {
      const next = sorted[i];
      if (next.start.valueOf() <= current.end.valueOf()) {
        if (next.end.valueOf() > current.end.valueOf()) {
          current.end = next.end;
        }
        current.hostCount += 1;
      } else {
        merged.push({ date, ...current });
        current = { start: next.start, end: next.end, hostCount: 1 };
      }
    }
    merged.push({ date, ...current });
  });
  return merged;
}

/** Split one busy interval at local midnight so each piece lands in its own
 * day column. A 23:00–01:00 block otherwise draws inside one column with a
 * negative height. */
function splitAtMidnight(
  band: UnavailableBand,
  tz: string,
): Array<{ start: Dayjs; end: Dayjs }> {
  const end = dayjs(band.end_at).tz(tz);
  const pieces: Array<{ start: Dayjs; end: Dayjs }> = [];
  let cursor = dayjs(band.start_at).tz(tz);
  while (cursor.isBefore(end)) {
    const nextMidnight = cursor.startOf('day').add(1, 'day');
    const stop = end.isBefore(nextMidnight) ? end : nextMidnight;
    pieces.push({ start: cursor, end: stop });
    cursor = nextMidnight;
  }
  return pieces;
}

/** Merge one host's busy intervals into non-overlapping windows per local day.
 *
 * Thor already unions the raw blocks, so this only catches a block that crosses
 * midnight in the display timezone — without the split, the second half would
 * land on the wrong day column. */
function mergeBusyWindows(
  bands: UnavailableBand[],
  tz: string,
): Array<{ date: string; start: Dayjs; end: Dayjs }> {
  const byDay = new Map<string, Array<{ start: Dayjs; end: Dayjs }>>();
  bands.forEach((band) => {
    splitAtMidnight(band, tz).forEach(({ start, end }) => {
      const key = start.format('YYYY-MM-DD');
      const rows = byDay.get(key) ?? [];
      rows.push({ start, end });
      byDay.set(key, rows);
    });
  });

  const merged: Array<{ date: string; start: Dayjs; end: Dayjs }> = [];
  byDay.forEach((rows, date) => {
    const sorted = rows
      .slice()
      .sort((a, b) => a.start.valueOf() - b.start.valueOf());
    let current = { ...sorted[0] };
    for (let i = 1; i < sorted.length; i += 1) {
      const next = sorted[i];
      if (next.start.valueOf() <= current.end.valueOf()) {
        if (next.end.valueOf() > current.end.valueOf()) {
          current.end = next.end;
        }
      } else {
        merged.push({ date, ...current });
        current = { ...next };
      }
    }
    merged.push({ date, ...current });
  });
  return merged;
}

const CalendarView: FC<CalendarViewProps> = ({
  weekStart,
  onWeekChange,
  timezone: tz,
  appointments,
  availability,
  unavailable = [],
  hosts,
  loading,
  error,
  onRetry,
  onAppointmentClick,
  onSlotClick,
  onAppointmentAction,
  hideHostFilter = false,
  showProperty = false,
}) => {
  const theme = useTheme();
  const [hostFilter, setHostFilter] = useState<number | 'all'>('all');
  const [actionMenu, setActionMenu] = useState<{
    anchor: HTMLElement;
    row: CalendarRow;
  } | null>(null);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day')),
    [weekStart],
  );

  const visibleAppointments = useMemo(
    () =>
      hostFilter === 'all'
        ? appointments
        : appointments.filter((a) => a.host_id === hostFilter),
    [appointments, hostFilter],
  );
  const visibleBands = useMemo(
    () =>
      hostFilter === 'all'
        ? availability
        : availability.filter((b) => b.host_id === hostFilter),
    [availability, hostFilter],
  );

  // One merged window per day instead of one translucent band per host —
  // twenty stacked 12% bands otherwise read as solid green and hide the
  // appointments underneath.
  const mergedBands = useMemo(
    () => mergeAvailabilityBands(visibleBands, tz),
    [visibleBands, tz]
  );

  // The host's own busy time (ENG-4235). Deliberately not host-filtered: every
  // surface that passes these bands is a single person's calendar.
  const busyWindows = useMemo(
    () => mergeBusyWindows(unavailable, tz),
    [unavailable, tz]
  );

  // Business-hours window: union of bands and appointments, ±1h padding.
  const { startHour, endHour } = useMemo(() => {
    let lo = DEFAULT_START_HOUR * 60;
    let hi = DEFAULT_END_HOUR * 60;
    const consider = (startIso: string, endIso: string): void => {
      const s = minutesSinceMidnight(dayjs(startIso).tz(tz));
      const eRaw = dayjs(endIso).tz(tz);
      const e =
        eRaw.hour() === 0 && eRaw.minute() === 0
          ? 24 * 60
          : minutesSinceMidnight(eRaw);
      lo = Math.min(lo, s);
      hi = Math.max(hi, e);
    };
    visibleBands.forEach((b) => consider(b.start_at, b.end_at));
    visibleAppointments.forEach((a) => consider(a.start_at, a.end_at));
    unavailable.forEach((b) => consider(b.start_at, b.end_at));
    return {
      startHour: Math.max(0, Math.floor(lo / 60) - 1),
      endHour: Math.min(24, Math.ceil(hi / 60) + 1),
    };
  }, [visibleBands, visibleAppointments, unavailable, tz]);

  const totalRows = ((endHour - startHour) * 60) / ROW_MINUTES;
  const gridHeight = totalRows * ROW_PX;

  const topFor = useCallback(
    (value: Dayjs): number =>
      ((minutesSinceMidnight(value) - startHour * 60) / ROW_MINUTES) * ROW_PX,
    [startHour],
  );

  const heightFor = useCallback(
    (start: Dayjs, end: Dayjs): number =>
      Math.max(ROW_PX, (end.diff(start, 'minute') / ROW_MINUTES) * ROW_PX),
    [],
  );

  const handlePrev = useCallback(
    () => onWeekChange(weekStart.subtract(7, 'day')),
    [onWeekChange, weekStart],
  );
  const handleNext = useCallback(
    () => onWeekChange(weekStart.add(7, 'day')),
    [onWeekChange, weekStart],
  );
  const handleToday = useCallback(() => {
    const today = dayjs().tz(tz);
    const dow = today.day();
    const offset = dow === 0 ? 6 : dow - 1;
    onWeekChange(today.subtract(offset, 'day').startOf('day'));
  }, [onWeekChange, tz]);

  const handleGridClick = useCallback(
    (bandStart: Dayjs, hostId: number | null, event: MouseEvent) => {
      // A merged band can cover several hosts, so prefilling a host only makes
      // sense when the grid is filtered to one.
      if (!onSlotClick || hostId === null) {
        return;
      }
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      const offsetMinutes =
        Math.floor((event.clientY - rect.top) / ROW_PX) * ROW_MINUTES;
      const clicked = bandStart.add(offsetMinutes, 'minute');
      onSlotClick(hostId, clicked);
    },
    [onSlotClick],
  );

  const todayKey = dayjs().tz(tz).format('YYYY-MM-DD');
  const zoneAbbr = zoneAbbreviation(tz);

  return (
    <Stack spacing={1.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            size="small"
            onClick={handlePrev}
            aria-label="Previous week"
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ minWidth: 220 }}>
            {weekStart.format('MMM D')} –{' '}
            {weekStart.add(6, 'day').format('MMM D, YYYY')}
          </Typography>
          <IconButton size="small" onClick={handleNext} aria-label="Next week">
            <ChevronRightIcon />
          </IconButton>
          <Button size="small" variant="text" onClick={handleToday}>
            Today
          </Button>
          <Typography variant="caption" color="text.secondary">
            Times in {zoneAbbr}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {!hideHostFilter && hosts.length > 0 && (
            <TextField
              select
              size="small"
              label="Host"
              value={hostFilter}
              onChange={(event) =>
                setHostFilter(
                  event.target.value === 'all'
                    ? 'all'
                    : Number(event.target.value),
                )
              }
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="all">All hosts</MenuItem>
              {hosts.map((h) => (
                <MenuItem key={h.id} value={h.id}>
                  {h.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {loading && <CircularProgress size={20} />}
        </Stack>
      </Stack>

      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${HOUR_COL_PX}px repeat(7, minmax(0, 1fr))`,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1.5,
          overflow: 'hidden',
        }}
      >
        {/* Header row */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.neutral',
          }}
        />
        {days.map((day) => {
          const isToday = day.format('YYYY-MM-DD') === todayKey;
          return (
            <Box
              key={day.toString()}
              sx={{
                borderBottom: 1,
                borderLeft: 1,
                borderColor: 'divider',
                bgcolor: isToday
                  ? alpha(theme.palette.primary.main, 0.08)
                  : 'background.neutral',
                px: 1,
                py: 0.75,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {day.format('ddd')}
              </Typography>
              <Typography variant="subtitle2" fontWeight={isToday ? 700 : 500}>
                {day.format('M/D')}
              </Typography>
            </Box>
          );
        })}

        {/* Hour gutter */}
        <Box sx={{ position: 'relative', height: gridHeight }}>
          {Array.from(
            { length: endHour - startHour },
            (_, i) => startHour + i,
          ).map((hour) => (
            <Typography
              key={hour}
              variant="caption"
              color="text.secondary"
              sx={{
                position: 'absolute',
                top: (((hour - startHour) * 60) / ROW_MINUTES) * ROW_PX - 7,
                right: 6,
                lineHeight: 1,
              }}
            >
              {dayjs().hour(hour).minute(0).format('h A')}
            </Typography>
          ))}
        </Box>

        {/* Day columns */}
        {days.map((day) => {
          const dayKey = day.format('YYYY-MM-DD');
          const bands = mergedBands.filter((b) => b.date === dayKey);
          const rows = visibleAppointments.filter(
            (a) => dayjs(a.start_at).tz(tz).format('YYYY-MM-DD') === dayKey,
          );
          return (
            <Box
              key={dayKey}
              sx={{
                position: 'relative',
                height: gridHeight,
                borderLeft: 1,
                borderColor: 'divider',
                backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent ${
                  ROW_PX * 4 - 1
                }px, ${theme.palette.divider} ${ROW_PX * 4 - 1}px, ${
                  theme.palette.divider
                } ${ROW_PX * 4}px)`,
              }}
            >
              {bands.map((band) => {
                const s = band.start;
                const e = band.end;
                return (
                  <Tooltip
                    key={band.start.toISOString()}
                    title={`${band.hostCount} ${
                      band.hostCount === 1 ? 'host' : 'hosts'
                    } available ${s.format('h:mm A')}–${e.format('h:mm A')}`}
                    placement="right"
                  >
                    <Box
                      onClick={(event) =>
                        handleGridClick(
                          band.start,
                          hostFilter === 'all' ? null : hostFilter,
                          event,
                        )
                      }
                      sx={{
                        position: 'absolute',
                        left: 2,
                        right: 2,
                        top: topFor(s),
                        height: heightFor(s, e),
                        bgcolor: alpha(theme.palette.success.main, 0.12),
                        borderLeft: 3,
                        borderColor: 'success.main',
                        cursor: onSlotClick ? 'copy' : 'default',
                        '&:hover': onSlotClick
                          ? { bgcolor: alpha(theme.palette.success.main, 0.2) }
                          : undefined,
                      }}
                    />
                  </Tooltip>
                );
              })}
              {busyWindows
                .filter((b) => b.date === dayKey)
                .map((band) => {
                  const s = band.start;
                  const e = band.end;
                  const timeRange = `${s.format('h:mm A')}–${e.format(
                    'h:mm A'
                  )}`;
                  return (
                    <Tooltip
                      key={`unavailable-${band.start.toISOString()}`}
                      title={`Unavailable ${timeRange}`}
                      placement="right"
                    >
                      <Box
                        data-testid="unavailable-band"
                        aria-label={`Unavailable ${timeRange}`}
                        sx={{
                          position: 'absolute',
                          left: 2,
                          right: 2,
                          top: topFor(s),
                          height: heightFor(s, e),
                          backgroundColor: alpha(
                            theme.palette.grey[500],
                            0.14
                          ),
                          backgroundImage: `repeating-linear-gradient(45deg, transparent 0, transparent 5px, ${alpha(
                            theme.palette.grey[500],
                            0.25
                          )} 5px, ${alpha(
                            theme.palette.grey[500],
                            0.25
                          )} 10px)`,
                          borderLeft: 3,
                          borderColor: 'grey.400',
                        }}
                      />
                    </Tooltip>
                  );
                })}
              {rows.map((row) => {
                const s = dayjs(row.start_at).tz(tz);
                const e = dayjs(row.end_at).tz(tz);
                const unitLabels = appointmentUnitLabels(
                  (row as CalendarRow & { units?: UnitOption[] }).units,
                );
                const paletteKey = STATUS_PALETTE[row.status];
                const color =
                  paletteKey === 'grey'
                    ? theme.palette.grey[500]
                    : theme.palette[paletteKey].main;
                return (
                  <Tooltip
                    key={row.id}
                    title={`${row.category_name}${
                      row.household_name ? ` — ${row.household_name}` : ''
                    } · ${s.format('h:mm A')}–${e.format('h:mm A')}${
                      row.host_name ? ` · ${row.host_name}` : ''
                    } · ${SOURCE_LABELS[row.source]}${
                      unitLabels.length
                        ? ` · Units: ${unitLabels.join(', ')}`
                        : ''
                    }`}
                  >
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={(event) => {
                        event.stopPropagation();
                        onAppointmentClick(row);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onAppointmentClick(row);
                        }
                      }}
                      sx={{
                        position: 'absolute',
                        left: 6,
                        right: 6,
                        top: topFor(s),
                        height: heightFor(s, e),
                        bgcolor: alpha(color, 0.18),
                        border: 1,
                        borderColor: color,
                        borderLeft: 4,
                        borderRadius: 0.75,
                        px: 0.75,
                        py: 0.25,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        opacity:
                          row.status === 'cancelled' ||
                          row.status === 'rescheduled'
                            ? 0.6
                            : 1,
                        '&:hover': { bgcolor: alpha(color, 0.28) },
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ display: 'block', lineHeight: 1.2 }}
                        noWrap
                      >
                        {s.format('h:mm')} {row.category_name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', lineHeight: 1.2 }}
                        noWrap
                      >
                        {showProperty && row.property_name
                          ? row.property_name
                          : (row.household_name ?? row.log_number ?? '—')}
                      </Typography>
                      {unitLabels.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', lineHeight: 1.2 }}
                          noWrap
                        >
                          Units: {unitLabels.slice(0, 2).join(', ')}
                          {unitLabels.length > 2
                            ? ` +${unitLabels.length - 2}`
                            : ''}
                        </Typography>
                      )}
                      {onAppointmentAction && row.status === 'scheduled' && (
                        <IconButton
                          size="small"
                          aria-label={`actions for ${row.category_name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setActionMenu({
                              anchor: event.currentTarget,
                              row,
                            });
                          }}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            p: 0,
                          }}
                        >
                          <MoreVertIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          );
        })}
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label="Available"
          sx={{
            bgcolor: alpha(theme.palette.success.main, 0.12),
            borderLeft: 3,
            borderColor: 'success.main',
            borderRadius: 0.5,
          }}
        />
        {unavailable.length > 0 && (
          <Chip
            size="small"
            label="Unavailable"
            sx={{
              backgroundColor: alpha(theme.palette.grey[500], 0.14),
              backgroundImage: `repeating-linear-gradient(45deg, transparent 0, transparent 5px, ${alpha(
                theme.palette.grey[500],
                0.25
              )} 5px, ${alpha(theme.palette.grey[500], 0.25)} 10px)`,
              borderLeft: 3,
              borderColor: 'grey.400',
              borderRadius: 0.5,
            }}
          />
        )}
        {(Object.keys(STATUS_PALETTE) as AppointmentStatus[])
          .filter((s) => s !== 'rescheduled')
          .map((status) => {
            const key = STATUS_PALETTE[status];
            return (
              <Chip
                key={status}
                size="small"
                variant="outlined"
                color={key === 'grey' ? 'default' : key}
                label={
                  status === 'no_show'
                    ? 'No-show'
                    : status[0].toUpperCase() + status.slice(1)
                }
              />
            );
          })}
      </Stack>

      <Menu
        anchorEl={actionMenu?.anchor ?? null}
        open={Boolean(actionMenu)}
        onClose={() => setActionMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (actionMenu) {
              onAppointmentAction?.(actionMenu.row, 'reschedule');
            }
            setActionMenu(null);
          }}
        >
          Reschedule
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionMenu) {
              onAppointmentAction?.(actionMenu.row, 'complete');
            }
            setActionMenu(null);
          }}
        >
          Complete
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionMenu) {
              onAppointmentAction?.(actionMenu.row, 'no_show');
            }
            setActionMenu(null);
          }}
        >
          No-show
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionMenu) {
              onAppointmentAction?.(actionMenu.row, 'cancel');
            }
            setActionMenu(null);
          }}
          sx={{ color: 'error.main' }}
        >
          Cancel
        </MenuItem>
      </Menu>
    </Stack>
  );
};

export default CalendarView;
